import { getFirestore, collection, getDocs } from 'firebase/firestore';

/**
 * Boilerplate Resolver Service
 * 
 * Resolves {{>boilerplateName}} handlebars in email templates by replacing them
 * with actual boilerplate content. Supports:
 * - Locale matching with fallback
 * - Nested boilerplate resolution
 * - Circular reference detection
 */

// Regex to match boilerplate handlebars: {{>name}} or {{> name}}
const BOILERPLATE_REGEX = /\{\{>\s*([a-zA-Z0-9_-]+)\s*\}\}/g;

// Maximum nesting depth to prevent stack overflow
const MAX_RECURSION_DEPTH = 3;

export interface ResolveOptions {
  locale: string;
  maxDepth?: number;
  onMissingBoilerplate?: 'warn' | 'error' | 'silent';
}

export interface ResolveResult {
  resolvedContent: string;
  warnings: string[];
  errors: string[];
  resolvedBoilerplates: string[];
}

export interface BoilerplateData {
  id: string;
  tag: string;
  template: string;
}

/**
 * Cache for boilerplates to avoid repeated Firestore calls during resolution
 */
let boilerplateCache: Map<string, BoilerplateData> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Load all boilerplates from Firestore into cache
 */
async function loadBoilerplatesIntoCache(): Promise<Map<string, BoilerplateData>> {
  const now = Date.now();
  
  // Return cached data if still valid
  if (boilerplateCache && (now - cacheTimestamp) < CACHE_TTL) {
    return boilerplateCache;
  }
  
  const db = getFirestore();
  const snaps = await getDocs(collection(db, 'emailBoilerplates'));
  
  boilerplateCache = new Map();
  snaps.docs.forEach(d => {
    const data = d.data();
    boilerplateCache!.set(data.tag, {
      id: d.id,
      tag: data.tag,
      template: data.template
    });
  });
  
  cacheTimestamp = now;
  return boilerplateCache;
}

/**
 * Clear the boilerplate cache (useful after edits)
 */
export function clearBoilerplateCache(): void {
  boilerplateCache = null;
  cacheTimestamp = 0;
}

/**
 * Extract all boilerplate references from content
 */
export function extractBoilerplateReferences(content: string): string[] {
  const references: string[] = [];
  let match;
  
  // Reset regex lastIndex
  BOILERPLATE_REGEX.lastIndex = 0;
  
  while ((match = BOILERPLATE_REGEX.exec(content)) !== null) {
    references.push(match[1]);
  }
  
  return Array.from(new Set(references)); // Remove duplicates
}

/**
 * Fetch a boilerplate by exact tag match only (no locale fallback)
 * 
 * @returns The boilerplate content and the actual tag used, or null if not found
 */
async function fetchBoilerplateWithFallback(
  name: string,
  locale: string,
  cache: Map<string, BoilerplateData>
): Promise<{ content: string; actualTag: string } | null> {
  // Try exact name only
  if (cache.has(name)) {
    return { content: cache.get(name)!.template, actualTag: name };
  }
  
  return null;
}

/**
 * Detect circular references in boilerplate chain
 * 
 * @throws Error if circular reference detected
 */
function checkCircularReference(
  boilerplateName: string,
  visited: Set<string>,
  path: string[]
): void {
  if (visited.has(boilerplateName)) {
    const cyclePath = [...path, boilerplateName].join(' → ');
    throw new Error(`Circular reference detected: ${cyclePath}`);
  }
}

/**
 * Resolve all boilerplate handlebars in content
 * 
 * Recursively resolves {{>boilerplateName}} patterns with actual boilerplate content.
 * Supports nested boilerplates up to MAX_NESTING_DEPTH levels.
 * 
 * @param content - The template content with boilerplate handlebars
 * @param options - Resolution options including locale and error handling
 * @returns ResolveResult with resolved content, warnings, and metadata
 */
export async function resolveBoilerplates(
  content: string,
  options: ResolveOptions
): Promise<ResolveResult> {
  const {
    locale,
    maxDepth = MAX_RECURSION_DEPTH,
    onMissingBoilerplate = 'warn'
  } = options;
  
  const warnings: string[] = [];
  const errors: string[] = [];
  const resolvedBoilerplates: string[] = [];
  
  // Load all boilerplates into cache
  const cache = await loadBoilerplatesIntoCache();
  
  // Internal recursive resolver
  async function resolveRecursive(
    text: string,
    visited: Set<string>,
    path: string[],
    depth: number
  ): Promise<string> {
    // Check depth limit
    if (depth > maxDepth) {
      const error = `Maximum nesting depth (${maxDepth}) exceeded at: ${path.join(' → ')}`;
      errors.push(error);
      throw new Error(error);
    }
    
    // Find all boilerplate references
    const references = extractBoilerplateReferences(text);
    
    if (references.length === 0) {
      return text;
    }
    
    let result = text;
    
    for (const refName of references) {
      // Check for circular reference
      try {
        checkCircularReference(refName, visited, path);
      } catch (e) {
        errors.push((e as Error).message);
        // Leave the handlebar as-is if circular
        continue;
      }
      
      // Fetch boilerplate with locale fallback
      const boilerplate = await fetchBoilerplateWithFallback(refName, locale, cache);
      
      if (!boilerplate) {
        // Boilerplate not found
        const warning = `Boilerplate "${refName}" not found`;
        if (onMissingBoilerplate === 'warn') {
          warnings.push(warning);
        } else if (onMissingBoilerplate === 'error') {
          errors.push(warning);
        }
        // Leave the handlebar as-is (will render as literal text)
        continue;
      }
      
      // Mark as resolved
      if (!resolvedBoilerplates.includes(boilerplate.actualTag)) {
        resolvedBoilerplates.push(boilerplate.actualTag);
      }
      
      // Add to visited set for this branch
      visited.add(refName);
      
      // Recursively resolve nested boilerplates in the fetched content
      const resolvedContent = await resolveRecursive(
        boilerplate.content,
        visited,
        [...path, refName],
        depth + 1
      );
      
      // Remove from visited (allow same boilerplate in different branches)
      visited.delete(refName);
      
      // Replace all occurrences of this boilerplate reference
      const pattern = new RegExp(`\\{\\{>\\s*${escapeRegex(refName)}\\s*\\}\\}`, 'g');
      result = result.replace(pattern, resolvedContent);
    }
    
    return result;
  }
  
  try {
    const resolvedContent = await resolveRecursive(content, new Set(), [], 0);
    
    return {
      resolvedContent,
      warnings,
      errors,
      resolvedBoilerplates
    };
  } catch (e) {
    // Return partial result with error
    return {
      resolvedContent: content,
      warnings,
      errors: [...errors, (e as Error).message],
      resolvedBoilerplates
    };
  }
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate template for boilerplate issues
 * 
 * Checks for:
 * - Missing boilerplate references
 * - Circular references
 * - Nesting depth issues
 * 
 * @returns Validation result with warnings and errors
 */
export async function validateTemplateBoilerplates(
  content: string,
  locale: string
): Promise<{ valid: boolean; warnings: string[]; errors: string[] }> {
  const result = await resolveBoilerplates(content, {
    locale,
    onMissingBoilerplate: 'warn'
  });
  
  return {
    valid: result.errors.length === 0,
    warnings: result.warnings,
    errors: result.errors
  };
}

/**
 * Get list of all available boilerplates
 * Useful for UI dropdowns
 */
export async function getAvailableBoilerplates(): Promise<BoilerplateData[]> {
  const cache = await loadBoilerplatesIntoCache();
  return Array.from(cache.values());
}

/**
 * Check if content contains any boilerplate handlebars
 */
export function hasBoilerplateHandlebars(content: string): boolean {
  BOILERPLATE_REGEX.lastIndex = 0;
  return BOILERPLATE_REGEX.test(content);
}

/**
 * Generate a boilerplate handlebar string
 */
export function generateBoilerplateHandlebar(boilerplateTag: string): string {
  return `{{>${boilerplateTag}}}`;
}
